import {
  getQualityInfo,
  getUnitSystem,
  convertFromMm,
  QUALITY_LABELS,
  type QualityInfo,
} from '@/config/printArea';
import type { RelativeDesignState } from '@designdeck/shared';

/**
 * Physical size of a guided placement.
 *
 * The Creator treats geometry as the truth and derives the physical size from
 * it: `widthMm = scaledWidthPx / pxPerMm`, with pxPerMm coming from the
 * product's print area (see utils/sizeCalculation.ts, which does the same for a
 * Fabric object). The guided entry has no Fabric object, but it has the same
 * two numbers — the placement's relative width and the print context — so the
 * derivation is identical.
 *
 * The seller can still type a size; that converts back through the same ratio,
 * exactly as ImagePropertiesBar does on the canvas.
 */

export interface GuidedPrintGeometry {
  /** Print area width in canvas pixels. */
  widthPx: number;
  /** Canvas pixels per millimetre, from the product's print area. */
  pxPerMm: number;
}

export interface GuidedSizeInfo {
  widthMm: number;
  heightMm: number;
  /** Width in the locale's display unit. */
  width: number;
  height: number;
  /** 'cm' or 'in'. */
  unit: string;
  displaySize: string;
  dpi: number;
  quality: QualityInfo;
  qualityLabel: string;
}

/**
 * Derive the printed size of a placement.
 *
 * @param motifWidthPx natural pixel width of the uploaded file, for DPI
 */
export function guidedSizeInfo(
  placement: RelativeDesignState,
  geometry: GuidedPrintGeometry,
  motifWidthPx: number,
  locale = 'de'
): GuidedSizeInfo | null {
  if (!(geometry.widthPx > 0) || !(geometry.pxPerMm > 0)) return null;

  const scaledWidthPx = placement.relWidth * geometry.widthPx;
  const widthMm = scaledWidthPx / geometry.pxPerMm;
  const heightMm = widthMm * placement.designRatio;
  if (!(widthMm > 0)) return null;

  const unitSystem = getUnitSystem(locale);
  const w = convertFromMm(widthMm, unitSystem);
  const h = convertFromMm(heightMm, unitSystem);

  // Same definition as calculateImageDPI: the file's own pixels stretched
  // across the physical width.
  const dpi = motifWidthPx > 0 ? motifWidthPx / (widthMm / 25.4) : 0;
  const quality = getQualityInfo(dpi);

  return {
    widthMm,
    heightMm,
    width: w.value,
    height: h.value,
    unit: w.unit,
    displaySize: `${w.value.toFixed(1)} × ${h.value.toFixed(1)} ${w.unit}`,
    dpi,
    quality,
    qualityLabel:
      QUALITY_LABELS[quality.level][locale.startsWith('de') ? 'de' : 'en'],
  };
}

/**
 * Inverse: the relative width a typed display size corresponds to.
 *
 * Returns null for a non-positive size, so a cleared input leaves the placement
 * untouched instead of collapsing it.
 */
export function relWidthForDisplaySize(
  displayWidth: number,
  geometry: GuidedPrintGeometry,
  locale = 'de'
): number | null {
  if (!(displayWidth > 0)) return null;
  if (!(geometry.widthPx > 0) || !(geometry.pxPerMm > 0)) return null;

  const unitSystem = getUnitSystem(locale);
  const widthMm =
    unitSystem === 'imperial' ? displayWidth * 25.4 : displayWidth * 10;

  return (widthMm * geometry.pxPerMm) / geometry.widthPx;
}
